import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import authModule from '@/store/modules/authModule';
import router from '../router';

class Client {
  httpClient: AxiosInstance;

  constructor() {
    this.httpClient = axios.create({
      baseURL: '/api/',
    });

    this.httpClient.interceptors.request.use(
      function(config) {
        const token = authModule.getAccessToken;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      function(error) {
        return Promise.reject(error);
      },
    );

    this.httpClient.interceptors.response.use(
      function(response) {
        return response;
      },
      async function(error) {
        const status: number | undefined = error.response && error.response.status;
        if (status === 401 && router.currentRoute.name !== 'login') {
          authModule.clear();
          // запоминаем роут, чтобы после логина вернуться обратно
          if (router.currentRoute.name) authModule.setPreviousRoute(router.currentRoute.path);
          await router.push('/login');
        }
        if (status === 500 || error.code === 'ECONNABORTED') {
          const responseData: string =
            error.response && error.response.data ? error.response.data : '';
          const addr: string = router.currentRoute.path;
          const addrErrorRequest: string =
            error.request && error.request.responseURL ? error.request.responseURL : '';
          const typeMethod: string = error.config && error.config.method;
          await router.push({
            name: 'emptyPage',
            params: {
              info: responseData,
              address: addr,
              addrErr: addrErrorRequest,
              method: typeMethod,
            },
          });
        }
        if (status === 404) {
          await router.push('/pageNotFound');
        }
        return Promise.reject(error);
      },
    );
  }

  async get<T = any, R = AxiosResponse<T>>(url: string, config?: AxiosRequestConfig): Promise<R> {
    try {
      return await this.httpClient.get<T, R>(url, config);
    } catch (e) {
      // почему-то не срабатывает интерцептор для get-запросов, обрабатываем вручную.
      // Для CORS / network / timeout ошибок e.response может быть undefined,
      // поэтому проверяем статус только при его наличии.
      if (e && e.response && 401 == e.response.status) {
        authModule.clear();
        // запоминаем роут, чтобы после логина вернуться обратно
        if (router.currentRoute.name) authModule.setPreviousRoute(router.currentRoute.path);
        await router.push('/login');
        return Promise.reject(e);
      }
      throw e;
    }
  }

  async put<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.httpClient.put<T, R>(url, data, config);
  }

  async patch<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.httpClient.patch<T, R>(url, data, config);
  }

  async post<T = any, R = AxiosResponse<T>>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.httpClient.post<T, R>(url, data, config);
  }

  async delete<T = any, R = AxiosResponse<T>>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<R> {
    return await this.httpClient.delete<T, R>(url, config);
  }
}

export const httpClient = new Client();

const getHTTP = () => window.location.protocol;

const getWS = () => (getHTTP() === 'https:' ? 'wss:' : 'ws:');

export function openWebSocket(path: string) {
  return new WebSocket(`${getWS()}//${window.location.host}/api/ws/${path}`);
}

export function openEventSource(path: string) {
  return new EventSource(`${getHTTP()}//${window.location.host}/api/sse/${path}`);
}
